import type {
  RAGConfig,
  RAGEnv,
  Document,
  Chunk,
  IngestionRequest,
  QueryRequest,
  QueryResponse,
  QueryStreamChunk,
  BatchProcessingResult,
  RAGPipelineInfo,
  RAGPipelineStatus,
  RAGAnalyticsEvent,
} from '../types';
import { RAGError } from '../types';
import { DocumentLoader } from '../processing/document-loader';
import { DocumentChunker } from '../processing/chunking';
import { EmbeddingService } from '../processing/embedding-service';
import { HybridRetriever } from '../retrieval/retriever';
import { AnswerGenerator } from '../generation/answer-generator';
import { generateId, hashContent } from '../core/utils';

/**
 * Main RAG pipeline that orchestrates all components
 */
export class RAGPipeline {
  private env: RAGEnv;
  private config: RAGConfig;
  private status: RAGPipelineStatus = 'initializing';
  private error?: string;

  // Core components
  private documentLoader: DocumentLoader;
  private chunker: DocumentChunker;
  private embeddingService: EmbeddingService;
  private retriever: HybridRetriever;
  private answerGenerator: AnswerGenerator;

  // Stats tracking
  private stats = {
    documentsProcessed: 0,
    chunksGenerated: 0,
    queriesProcessed: 0,
    totalProcessingTime: 0,
    avgResponseTime: 0,
    lastUsed: 0,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  constructor(env: RAGEnv, config: RAGConfig) {
    this.env = env;
    this.config = config;
    
    // Initialize components
    this.documentLoader = new DocumentLoader(env);
    this.chunker = new DocumentChunker();
    this.embeddingService = EmbeddingService.createForModel(
      env,
      config.embedding.model,
      config.embedding.provider
    );
    this.retriever = new HybridRetriever(env, this.embeddingService);
    this.answerGenerator = new AnswerGenerator(env, config);
  }

  /**
   * Initialize the RAG pipeline
   */
  async initialize(): Promise<void> {
    try {
      this.status = 'initializing';
      
      // Initialize all components
      await this.retriever.initialize();
      
      // Create database tables if they don't exist
      await this.createTables();
      
      // Store pipeline configuration
      await this.storePipelineConfig();
      
      this.status = 'ready';
    } catch (error) {
      this.status = 'error';
      this.error = error instanceof Error ? error.message : 'Unknown initialization error';
      throw new RAGError(
        `Pipeline initialization failed: ${this.error}`,
        'INITIALIZATION_ERROR',
        500,
        this.config.name
      );
    }
  }

  /**
   * Ingest documents into the RAG pipeline
   */
  async ingest(request: IngestionRequest | IngestionRequest[]): Promise<BatchProcessingResult> {
    const startTime = Date.now();
    
    try {
      if (this.status !== 'ready') {
        throw new RAGError('Pipeline not ready', 'PIPELINE_NOT_READY', 503, this.config.name);
      }
      
      this.status = 'processing';
      const requests = Array.isArray(request) ? request : [request];
      const results: BatchProcessingResult = {
        results: [],
        totalDocuments: requests.length,
        totalChunks: 0,
        totalTokens: 0,
        processingTime: 0,
        errors: [],
      };

      // Process documents in batches
      const batchSize = 10;
      for (let i = 0; i < requests.length; i += batchSize) {
        const batch = requests.slice(i, i + batchSize);
        const batchResults = await this.processBatch(batch);
        
        results.results.push(...batchResults.results);
        results.totalChunks += batchResults.totalChunks;
        results.totalTokens += batchResults.totalTokens;
        results.errors.push(...batchResults.errors);
      }

      // Update stats
      results.processingTime = Date.now() - startTime;
      this.stats.documentsProcessed += results.totalDocuments;
      this.stats.chunksGenerated += results.totalChunks;
      this.stats.totalProcessingTime += results.processingTime;
      this.stats.updatedAt = Date.now();

      // Record analytics
      await this.recordAnalytics({
        type: 'ingestion',
        timestamp: Date.now(),
        pipelineName: this.config.name,
        requestId: generateId('ingest'),
        metadata: {
          documentsCount: results.totalDocuments,
          chunksCount: results.totalChunks,
          processingTime: results.processingTime,
        },
      });

      this.status = 'ready';
      return results;
    } catch (error) {
      this.status = 'error';
      this.error = error instanceof Error ? error.message : 'Unknown ingestion error';
      throw error;
    }
  }

  /**
   * Query the RAG pipeline
   */
  async query(request: QueryRequest): Promise<QueryResponse> {
    const startTime = Date.now();
    const requestId = generateId('query');
    
    try {
      if (this.status !== 'ready') {
        throw new RAGError('Pipeline not ready', 'PIPELINE_NOT_READY', 503, this.config.name);
      }

      // Step 1: Retrieve relevant context
      const context = await this.retriever.retrieveContext(request.query, {
        topK: request.topK,
        minSimilarity: request.minSimilarity,
        hybridWeight: this.config.retrieval.hybridWeight,
        rerankResults: request.rerankResults,
        maxContextTokens: this.config.retrieval.maxContextTokens,
        filter: request.filter,
        namespace: request.namespace,
      });

      // Step 2: Generate answer
      const response = await this.answerGenerator.generateAnswer(
        request.query,
        context,
        {
          conversationHistory: request.conversationHistory,
          customPrompt: request.customPrompt,
          includeSource: request.includeSource,
          temperature: this.config.generation.temperature,
          maxTokens: this.config.generation.maxTokens,
        }
      );

      // Update stats
      const totalTime = Date.now() - startTime;
      this.stats.queriesProcessed++;
      this.stats.avgResponseTime = (
        (this.stats.avgResponseTime * (this.stats.queriesProcessed - 1)) + totalTime
      ) / this.stats.queriesProcessed;
      this.stats.lastUsed = Date.now();
      this.stats.updatedAt = Date.now();

      // Record analytics
      await this.recordAnalytics({
        type: 'query',
        timestamp: Date.now(),
        pipelineName: this.config.name,
        requestId,
        metadata: {
          query: request.query,
          responseTime: totalTime,
          retrievalTime: context.retrievalTime,
          generationTime: response.performance.generationTime,
          sourcesCount: response.sources.length,
          tokensUsed: response.usage.totalTokens,
        },
      });

      return response;
    } catch (error) {
      // Record error analytics
      await this.recordAnalytics({
        type: 'error',
        timestamp: Date.now(),
        pipelineName: this.config.name,
        requestId,
        metadata: {
          query: request.query,
          error: error instanceof Error ? error.message : 'Unknown error',
          errorCode: error instanceof RAGError ? error.code : 'UNKNOWN_ERROR',
        },
      });

      throw error;
    }
  }

  /**
   * Query with streaming response
   */
  async queryStream(
    request: QueryRequest,
    onChunk?: (chunk: QueryStreamChunk) => void
  ): Promise<ReadableStream<QueryStreamChunk>> {
    const startTime = Date.now();
    const requestId = generateId('stream');
    
    try {
      if (this.status !== 'ready') {
        throw new RAGError('Pipeline not ready', 'PIPELINE_NOT_READY', 503, this.config.name);
      }

      // Step 1: Retrieve relevant context
      const context = await this.retriever.retrieveContext(request.query, {
        topK: request.topK,
        minSimilarity: request.minSimilarity,
        hybridWeight: this.config.retrieval.hybridWeight,
        rerankResults: request.rerankResults,
        maxContextTokens: this.config.retrieval.maxContextTokens,
        filter: request.filter,
        namespace: request.namespace,
      });

      // Step 2: Generate streaming answer
      const stream = await this.answerGenerator.generateStreamingAnswer(
        request.query,
        context,
        {
          conversationHistory: request.conversationHistory,
          customPrompt: request.customPrompt,
          includeSource: request.includeSource,
          temperature: this.config.generation.temperature,
          maxTokens: this.config.generation.maxTokens,
          onChunk: (chunk) => {
            onChunk?.(chunk);
            
            // Record analytics when streaming completes
            if (chunk.type === 'complete') {
              const totalTime = Date.now() - startTime;
              this.stats.queriesProcessed++;
              this.stats.avgResponseTime = (
                (this.stats.avgResponseTime * (this.stats.queriesProcessed - 1)) + totalTime
              ) / this.stats.queriesProcessed;
              this.stats.lastUsed = Date.now();
              this.stats.updatedAt = Date.now();
              
              this.recordAnalytics({
                type: 'query',
                timestamp: Date.now(),
                pipelineName: this.config.name,
                requestId,
                metadata: {
                  query: request.query,
                  responseTime: totalTime,
                  retrievalTime: context.retrievalTime,
                  generationTime: chunk.data?.performance?.generationTime || 0,
                  sourcesCount: context.sources.length,
                  
                },
              });
            }
          },
        }
      );

      return stream;
    } catch (error) {
      // Record error analytics
      await this.recordAnalytics({
        type: 'error',
        timestamp: Date.now(),
        pipelineName: this.config.name,
        requestId,
        metadata: {
          query: request.query,
          error: error instanceof Error ? error.message : 'Unknown error',
          errorCode: error instanceof RAGError ? error.code : 'UNKNOWN_ERROR',
          
        },
      });

      throw error;
    }
  }

  /**
   * Process a batch of documents
   */
  private async processBatch(requests: IngestionRequest[]): Promise<BatchProcessingResult> {
    const results: BatchProcessingResult = {
      results: [],
      totalDocuments: requests.length,
      totalChunks: 0,
      totalTokens: 0,
      processingTime: 0,
      errors: [],
    };

    for (const request of requests) {
      try {
        const startTime = Date.now();
        
        // Step 1: Load document
        const document = await this.documentLoader.loadDocument(request);
        
        // Check for duplicates
        const contentHash = hashContent(document.content);
        const existingDoc = await this.checkDuplicate(contentHash);
        if (existingDoc) {
          console.log(`Skipping duplicate document: ${document.id}`);
          continue;
        }

        // Step 2: Chunk document
        const chunkConfig = DocumentChunker.getOptimizedConfig(document.metadata.format);
        const chunks = await this.chunker.chunkDocument(document, {
          ...chunkConfig,
          ...this.config.chunking,
        });

        // Step 3: Generate embeddings
        const embeddings = await this.embeddingService.generateEmbeddings(chunks);
        
        // Add embeddings to chunks
        chunks.forEach((chunk, index) => {
          chunk.embedding = embeddings[index];
        });

        // Step 4: Store in retriever
        await this.retriever.storeChunks(chunks, request.metadata?.namespace as string | undefined);
        await this.retriever.storeDocumentMetadata(document.metadata);
        
        // Step 5: Store content hash
        await this.storeContentHash(contentHash, document.id);

        const processingTime = Date.now() - startTime;
        const tokenCount = chunks.reduce((sum, chunk) => sum + chunk.tokens, 0);

        results.results.push({
          document,
          chunks,
          embeddings,
          processingTime,
          tokenCount,
        });

        results.totalChunks += chunks.length;
        results.totalTokens += tokenCount;
      } catch (error) {
        results.errors.push({
          documentId: `unknown`,
          error: error instanceof Error ? error.message : 'Unknown processing error',
        });
      }
    }

    return results;
  }

  /**
   * Get pipeline information and statistics
   */
  async getInfo(): Promise<RAGPipelineInfo> {
    const retrievalStats = await this.retriever.getStats();
    
    return {
      name: this.config.name,
      status: this.status,
      config: this.config,
      stats: {
        documentsCount: retrievalStats.totalDocuments,
        chunksCount: retrievalStats.totalChunks,
        totalQueries: this.stats.queriesProcessed,
        avgResponseTime: this.stats.avgResponseTime,
        lastUsed: this.stats.lastUsed,
        createdAt: this.stats.createdAt,
        updatedAt: this.stats.updatedAt,
      },
      ...(this.error ? { error: this.error } : {}),
    };
  }

  /**
   * Update pipeline configuration
   */
  async updateConfig(newConfig: Partial<RAGConfig>): Promise<void> {
    this.config = { ...this.config, ...newConfig };
    
    // Update component configurations
    if (newConfig.embedding) {
      this.embeddingService.updateConfig(newConfig.embedding);
    }
    
    if (newConfig.generation) {
      this.answerGenerator.updateConfig(newConfig.generation);
    }
    
    // Store updated configuration
    await this.storePipelineConfig();
    
    this.stats.updatedAt = Date.now();
  }

  /**
   * Delete the pipeline and all its data
   */
  async delete(): Promise<void> {
    try {
      // Delete pipeline configuration
      await this.env.RAG_KV.delete(`pipeline:${this.config.name}:config`);
      
      // Delete content hashes
      await this.env.RAG_KV.delete(`pipeline:${this.config.name}:hashes`);
      
      // Clean up retriever data would need to be implemented
      // This would involve deleting all chunks and documents for this pipeline
      
      this.status = 'disabled';
    } catch (error) {
      throw new RAGError(
        `Failed to delete pipeline: ${error}`,
        'DELETE_ERROR',
        500,
        this.config.name
      );
    }
  }

  /**
   * Create necessary database tables
   */
  private async createTables(): Promise<void> {
    // Create documents table
    await this.env.VECTOR_DB.exec(`
      CREATE TABLE IF NOT EXISTS documents (
        document_id TEXT PRIMARY KEY,
        metadata TEXT NOT NULL,
        content_hash TEXT,
        created_at INTEGER NOT NULL,
        updated_at INTEGER NOT NULL
      )
    `);

    // Create chunks table
    await this.env.VECTOR_DB.exec(`
      CREATE TABLE IF NOT EXISTS chunks (
        chunk_id TEXT PRIMARY KEY,
        document_id TEXT NOT NULL,
        content TEXT NOT NULL,
        tokens INTEGER NOT NULL,
        metadata TEXT NOT NULL,
        namespace TEXT DEFAULT 'default',
        created_at INTEGER NOT NULL,
        FOREIGN KEY (document_id) REFERENCES documents(document_id)
      )
    `);

    // Create indexes
    await this.env.VECTOR_DB.exec(`
      CREATE INDEX IF NOT EXISTS idx_chunks_document_id ON chunks(document_id)
    `);
    await this.env.VECTOR_DB.exec(`
      CREATE INDEX IF NOT EXISTS idx_chunks_namespace ON chunks(namespace)
    `);
    await this.env.VECTOR_DB.exec(`
      CREATE INDEX IF NOT EXISTS idx_documents_hash ON documents(content_hash)
    `);
  }

  /**
   * Store pipeline configuration in KV
   */
  private async storePipelineConfig(): Promise<void> {
    await this.env.RAG_KV.put(
      `pipeline:${this.config.name}:config`,
      JSON.stringify({
        config: this.config,
        stats: this.stats,
        status: this.status,
        error: this.error,
      })
    );
  }

  /**
   * Check for duplicate content
   */
  private async checkDuplicate(contentHash: string): Promise<boolean> {
    try {
      const result = await this.env.VECTOR_DB
        .prepare('SELECT document_id FROM documents WHERE content_hash = ?')
        .bind(contentHash)
        .first();
      
      return !!result;
    } catch (error) {
      console.warn('Duplicate check failed:', error);
      return false;
    }
  }

  /**
   * Store content hash for deduplication
   */
  private async storeContentHash(contentHash: string, documentId: string): Promise<void> {
    try {
      await this.env.VECTOR_DB
        .prepare('UPDATE documents SET content_hash = ? WHERE document_id = ?')
        .bind(contentHash, documentId)
        .run();
    } catch (error) {
      console.warn('Failed to store content hash:', error);
    }
  }

  /**
   * Record analytics event
   */
  private async recordAnalytics(event: RAGAnalyticsEvent): Promise<void> {
    if (!this.config.analytics.enabled) return;
    
    // Sample events based on sample rate
    if (Math.random() > this.config.analytics.sampleRate) return;

    try {
      await this.env.ANALYTICS.writeDataPoint({
        blobs: [
          event.type,
          event.pipelineName,
          event.requestId,
          event.userId || '',
        ],
        doubles: [
          event.timestamp,
          event.metadata.responseTime || 0,
          event.metadata.retrievalTime || 0,
          event.metadata.generationTime || 0,
          event.metadata.tokensUsed || 0,
          event.metadata.sourcesCount || 0,
          event.metadata.documentsCount || 0,
          event.metadata.chunksCount || 0,
          event.metadata.processingTime || 0,
        ],
        indexes: [
          event.type,
          event.pipelineName,
        ],
      });
    } catch (error) {
      console.warn('Failed to record analytics:', error);
    }
  }

  /**
   * Get pipeline status
   */
  getStatus(): RAGPipelineStatus {
    return this.status;
  }

  /**
   * Check if pipeline is ready
   */
  isReady(): boolean {
    return this.status === 'ready';
  }

  /**
   * Get pipeline configuration
   */
  getConfig(): RAGConfig {
    return { ...this.config };
  }
}